import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRightLeft, Trash2, X, Warehouse, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FormModal, Field, SectionTitle } from "@/pages/InwardStock";

const emptyForm = { batch_number: "", from_location_id: "", to_location_id: "", quantity: 1, transfer_date: new Date().toISOString().slice(0, 10), notes: "" };

export default function StockTransfers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [byLoc, setByLoc] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [t, l, s] = await Promise.all([api.get("/stock-transfers"), api.get("/locations"), api.get("/stock/by-location")]);
    setItems(t.data); setLocations(l.data); setByLoc(s.data);
  };
  useEffect(() => { load(); }, []);

  const close = () => { setShowForm(false); setForm(emptyForm); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stock-transfers", { ...form, quantity: parseInt(form.quantity) });
      toast.success("Stock transferred");
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Undo this transfer?")) return;
    try { await api.delete(`/stock-transfers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const availableAtFrom = byLoc
    .filter((r) => r.batch_number === form.batch_number && r.location_id === form.from_location_id)
    .reduce((s, r) => s + (r.in_hand || 0), 0);

  const batchOptions = [...new Set(byLoc.filter((r) => r.in_hand > 0).map((r) => r.batch_number))];

  return (
    <div className="p-8 space-y-6" data-testid="transfers-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#28B516] mr-2 align-middle" />Movement</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Stock Transfers</h1>
          <div className="text-xs text-zinc-500 mt-2">Move batches between locations</div>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-transfer-btn"><Plus className="w-4 h-4" /> New Transfer</button>
      </div>

      <div className="border border-zinc-200 bg-white rounded-2xl">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-3">
          <Warehouse className="w-4 h-4 text-[#28B516]" />
          <div className="font-display text-lg uppercase text-zinc-900">Stock by Location</div>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="by-location-table">
            <thead><tr><th>Batch</th><th>Location</th><th>Brand / Model</th><th>Spec</th><th className="text-right">In</th><th className="text-right">Xfer In</th><th className="text-right">Xfer Out</th><th className="text-right">Sold</th><th className="text-right">In Hand</th></tr></thead>
            <tbody>
              {byLoc.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-zinc-500">No stock yet</td></tr>}
              {byLoc.map((r, i) => (
                <tr key={`${r.batch_number}-${r.location_id}-${i}`}>
                  <td className="font-mono font-bold text-[#28B516]">{r.batch_number}</td>
                  <td className="font-semibold">{r.location_name}</td>
                  <td>{r.brand} / {r.model}</td>
                  <td>{r.voltage}V · {r.capacity_ah}Ah · {r.battery_type}</td>
                  <td className="text-right font-mono">{r.in_qty}</td>
                  <td className="text-right font-mono">{r.transfer_in}</td>
                  <td className="text-right font-mono">{r.transfer_out}</td>
                  <td className="text-right font-mono">{r.out_qty}</td>
                  <td className={`text-right font-bold ${r.in_hand === 0 ? "text-[#D9363E]" : "text-[#28B516]"}`}>{r.in_hand}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white rounded-2xl">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-3">
          <ArrowRightLeft className="w-4 h-4 text-[#1769AA]" />
          <div className="font-display text-lg uppercase text-zinc-900">Transfer History</div>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="transfers-table">
            <thead><tr><th>Date</th><th>Batch</th><th>From</th><th>To</th><th className="text-right">Qty</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-zinc-500">No transfers yet</td></tr>}
              {items.map((x) => (
                <tr key={x.id}>
                  <td>{x.transfer_date}</td>
                  <td className="font-mono font-bold text-[#28B516]">{x.batch_number}</td>
                  <td className="font-semibold">{x.from_location_name}</td>
                  <td className="font-semibold">{x.to_location_name}</td>
                  <td className="text-right font-mono">{x.quantity}</td>
                  <td className="text-zinc-500">{x.notes || "—"}</td>
                  <td>{(user?.role === "admin" || x.created_by === user?.email) && <button onClick={() => remove(x.id)} className="text-zinc-400 hover:text-[#D9363E] transition-colors" data-testid={`del-transfer-${x.id}`}><Trash2 className="w-4 h-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <FormModal onClose={close}>
          <form onSubmit={submit} className="space-y-5" data-testid="transfer-form">
            <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
              <h2 className="font-display text-2xl uppercase text-zinc-900">New Stock Transfer</h2>
              <button type="button" onClick={close} className="text-zinc-500 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>
            <SectionTitle>Transfer Details</SectionTitle>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Batch*">
                <select required value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} className="input-field font-mono" data-testid="t-batch">
                  <option value="">Select batch…</option>
                  {batchOptions.map((b) => <option key={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="From Location*">
                <select required value={form.from_location_id} onChange={(e) => setForm({ ...form, from_location_id: e.target.value })} className="input-field" data-testid="t-from">
                  <option value="">Select…</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
                </select>
              </Field>
              <Field label="To Location*">
                <select required value={form.to_location_id} onChange={(e) => setForm({ ...form, to_location_id: e.target.value })} className="input-field" data-testid="t-to">
                  <option value="">Select…</option>
                  {locations.filter((l) => l.id !== form.from_location_id).map((l) => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
                </select>
              </Field>
              <Field label="Quantity*"><input required type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="input-field font-mono" data-testid="t-qty" /></Field>
              <Field label="Available at Source"><div className="input-field font-mono text-[#28B516] font-bold" data-testid="t-available">{form.batch_number && form.from_location_id ? availableAtFrom : "—"}</div></Field>
              <Field label="Transfer Date"><input type="date" value={form.transfer_date} onChange={(e) => setForm({ ...form, transfer_date: e.target.value })} className="input-field" data-testid="t-date" /></Field>
              <Field label="Notes" full><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field h-20 resize-none" data-testid="t-notes" /></Field>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
              <button type="button" onClick={close} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="submit-transfer">Transfer Stock</button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  );
}
